import React, { useEffect, useState } from "react";
import classNames from "classnames/bind";
import useSWR from "swr";
import { useLocalStorage } from "react-use";

import { Tile } from "../../types/Tile";
import { Portion } from "../../types/Portion";

import { Screen } from "../../components/Screen";
import { Tutorial } from "../../components/Tutorial";
import { DietanamnezTile } from "../../components/DietanamnezTile";
import { WelcomeBack } from "../../screens/WelcomeBack";

import { DietanamnezItemForm } from "types/DietanamnezItem";

import { getVisibleTiles } from "./utils/getVisibleTiles";
import { useStateManager } from "utils/useStateManager";

import styles from "./DietanamnezTiles.module.css";
import { sendFactpitStartButtonMetric } from "api/sendBIMetrics";
import { getQueryVariable } from "utils/getQueryVariable";

const cx = classNames.bind(styles);

type Props = {
  onBack: () => void;
  onSubmit: (exists?: boolean) => void;
};

export const DietanamnezTiles: React.FC<Props> = ({ onBack, onSubmit }) => {
  const {
    isInitial,
    setIsInitial,
    userType,
    dietanamnez,
    dietanamnezItems,
    setDietanamnezItem,
    step,
    setStep,
  } = useStateManager();
  const [tutorialWasShown = false, setTutorialWasShown] = useLocalStorage(
    "TUTORIAL_WAS_SHOWN",
    false
  );
  const { data: allTiles = [] } = useSWR<Tile[]>([49, 104]);
  const { data: allPortions = [] } = useSWR<Portion[]>([66, 149]);
  const tiles = getVisibleTiles(allTiles, dietanamnez!, userType);

  const stateManager = useStateManager();
  const { onNextScreen, setIsLoading } = stateManager;

  const tile = tiles?.[step] as Tile | undefined;
  const dietanamnezItem = tile?.key
    ? dietanamnezItems.find((item) => item.custom_блюдо === tile.key)
    : undefined;

  const portions = allPortions
    .filter((item) => item.___parent.key === tile?.key)
    .map((item) => item.custom_мера);

  const title = (
    <span className={cx("title")}>
      {step + 1}{" "}
      <span className={cx("titleAccent")}>
        вопрос {tiles.length ? `из ${tiles.length}` : ""}
      </span>
    </span>
  );

  const handleBack = () => {
    if (step === 0) {
      onBack();
    } else {
      setStep(step - 1);
    }
  };

  const handleForward = async () => {
    if (step >= tiles.length - 1) {
      await onSubmit(false);
    } else {
      setStep(step + 1);
    }
  };

  const [errorMessage, setErrorMessage] = useState<{ name: string, message: string }>({ name: "", message: "" });

  useEffect(() => {
    if (errorMessage.name) {
      const errorMessageTimer = setTimeout(() => {
        setErrorMessage({ name: "", message: "" })
      }, 5000);

      return () => {
        clearTimeout(errorMessageTimer);
      }
    }
  }, [errorMessage]);

  const handleSubmit = async (values?: DietanamnezItemForm) => {
    try {
      await setDietanamnezItem(tile!.key, values);

      const isInvalidDietanamnez = dietanamnezItems.length === 0;
      if ((step === tiles.length - 1) && isInvalidDietanamnez) {
        throw new Error("Dietanamnez Error");
      }

      await handleForward();
    } catch (error: any) {
      if (error.message === 'Network Error') {
        setErrorMessage({ name: error.message, message: "Ошибка подключения сети" });
      }
      // @ts-ignore
      if (error.message === "Dietanamnez Error") {
        setErrorMessage({ name: error.message, message: "Для завершения опроса укажите хотя бы одно блюдо, употребляемое чаще 1 раза в год" });
      }
      // @ts-ignore
      if (error.response?.data.exception.message.includes('уже зарегистрирован')) {
        setIsLoading(true);
        await onNextScreen(true);
        setIsLoading(false);
      }
    }
  };

  if (isInitial) {
    return <WelcomeBack onSubmit={() => setIsInitial(false)} />;
  }

  return (
    <Screen
      title={title}
      onBack={handleBack}
      onForward={dietanamnezItem ? handleForward : undefined}
    >
      {tile && (
        <>
          <DietanamnezTile
            key={tile.key}
            initialValues={dietanamnezItem}
            tile={tile}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            onSubmit={handleSubmit}
            portions={portions}
            tutorialWasShown={tutorialWasShown}
          />
          {tutorialWasShown || (
            <Tutorial onHide={async () => {
              const token = getQueryVariable('r');
              if (token) await sendFactpitStartButtonMetric(token);
              setTutorialWasShown(true)
            }} />
          )}
        </>
      )}
    </Screen>
  );
};
